import React, { useState } from 'react';
import { Plus } from 'lucide-react';

// UI Components
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Internal imports
import { useCreateList } from '../hooks/use-lists';
import { LIST_TYPE_OPTIONS } from '../constants/list-types';
import { CreateListRequest } from '@/types/lists.types';
import { CITY_OPTIONS } from '@/constants/cities';

interface CreateListModalProps {
  trigger?: React.ReactNode;
}

export const CreateListModal: React.FC<CreateListModalProps> = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreateListRequest>({
    listName: '',
    listType: 'custom',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const createList = useCreateList();

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.listName.trim()) {
      newErrors.listName = 'List name is required';
    } else if (formData.listName.trim().length < 2) {
      newErrors.listName = 'List name must be at least 2 characters';
    } else if (formData.listName.trim().length > 100) {
      newErrors.listName = 'List name must be less than 100 characters';
    }

    if (!formData.listType) {
      newErrors.listType = 'List type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createList.mutateAsync({
        ...formData,
        listName: formData.listName.trim(),
      });

      // Reset form and close modal
      setFormData({ listName: '', listType: 'custom' });
      setErrors({});
      setOpen(false);
    } catch (error) {
      // Error handling is done in the hook
      console.error('Failed to create list:', error);
    }
  };

  const handleInputChange = (field: keyof CreateListRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when closing
      setFormData({ listName: '', listType: 'custom' });
      setErrors({});
    }
  };

  const defaultTrigger = (
    <Button className="flex items-center gap-2">
      <Plus className="h-4 w-4" />
      Create New List
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] min-h-[350px]">
        <DialogHeader>
          <DialogTitle>Create New List</DialogTitle>
          <DialogDescription>
            Create a new list to organize your restaurants. Featured lists are visible to friends, while custom lists are private.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="listName">List Name</Label>
            <Input
              id="listName"
              placeholder=""
              value={formData.listName}
              onChange={(e) => handleInputChange('listName', e.target.value)}
              className={errors.listName ? 'border-brand' : ''}
            />
            {errors.listName && (
              <p className="text-sm text-brand">{errors.listName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="listType">List Type</Label>
            <Select
              value={formData.listType}
              onValueChange={(value) => handleInputChange('listType', value)}
            >
              <SelectTrigger className={errors.listType ? 'border-brand' : ''}>
                <SelectValue placeholder="Select a list type" />
              </SelectTrigger>
              <SelectContent>
                {LIST_TYPE_OPTIONS.filter(
                  (type) => type.value === 'featured' || type.value === 'custom'
                ).map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{type.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {type.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.listType && (
              <p className="text-sm text-brand">{errors.listType}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">City (Optional)</Label>
            <Select
              value={formData.city || ''}
              onValueChange={(value) => handleInputChange('city', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a city" />
              </SelectTrigger>
              <SelectContent>
                {CITY_OPTIONS.map((city) => (
                  <SelectItem key={city.value} value={city.value}>
                    {city.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createList.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createList.isPending || !formData.listName.trim()}
            >
              {createList.isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Creating...
                </>
              ) : (
                'Create List'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};